/**
 * messageUtils.ts - Utilitários de transformação de mensagens Uazapi.
 * Funções puras para normalização, detecção de remetente e mapeamento de tipos.
 */
import { UazapiMessage } from './types';

/** Normaliza telefone para formato JID do WhatsApp */
export function normalizePhoneToJid(phone: string): string {
    if (phone.includes('@')) return phone;
    const digits = phone.replace(/\D/g, '');
    return `${digits}@s.whatsapp.net`;
}

/**
 * Determina se a mensagem é fromMe.
 * Trata diversos formatos: boolean, number (0/1), string ("true"/"1").
 * Inclui fallback comparando sender/sender_pn com ownerJid para
 * corrigir atribuição incorreta da Uazapi no history sync.
 */
export function isFromMe(msg: UazapiMessage, ownerJid: string): boolean {
    const ownerDigits = ownerJid.replace(/\D/g, '');

    // 1. Campo fromMe (diversos formatos do Uazapi)
    let explicitFromMe: boolean | null = null;
    if (msg.fromMe !== undefined && msg.fromMe !== null) {
        if (typeof msg.fromMe === 'boolean') explicitFromMe = msg.fromMe;
        else if (typeof msg.fromMe === 'number') explicitFromMe = msg.fromMe === 1;
        else if (typeof msg.fromMe === 'string') explicitFromMe = msg.fromMe === 'true' || msg.fromMe === '1';
        else explicitFromMe = !!msg.fromMe;
    }

    // Se fromMe=true, confia diretamente
    if (explicitFromMe === true) return true;
    if (explicitFromMe === false) return false;

    const keyFromMe = (msg as any).key?.fromMe;
    if (keyFromMe === true || keyFromMe === 'true' || keyFromMe === 1 || keyFromMe === '1') return true;
    if (keyFromMe === false || keyFromMe === 'false' || keyFromMe === 0 || keyFromMe === '0') return false;

    const chatid = ((msg.chatid || (msg as any).key?.remoteJid || '') as string).toLowerCase();
    const isGroupOrLid = !!(msg as any).isGroup || chatid.includes('@g.us') ||
        String(msg.sender || '').includes('@lid') || String((msg as any).sender_lid || '').includes('@lid');

    if (ownerDigits && isGroupOrLid) {
        const senderPn = ((msg as any).sender_pn || '').toString();
        const senderPnDigits = senderPn.replace('@s.whatsapp.net', '').replace(/\D/g, '');
        if (senderPnDigits && senderPnDigits === ownerDigits) return true;

        const senderDigits = (msg.sender || '').replace('@s.whatsapp.net', '').replace(/\D/g, '');
        if (senderDigits && senderDigits === ownerDigits) return true;

        const msgOwner = ((msg as any).owner || '').toString().replace(/\D/g, '');
        const msgSenderDigits = (msg.sender || '').replace(/\D/g, '');
        if (msg.sender?.includes('@s.whatsapp.net') && msgSenderDigits === msgOwner && msgOwner === ownerDigits) return true;
    }

    return false;
}

/** Mapeia messageType do Uazapi para tipo interno do Adlitics */
export function mapMessageType(uazapiType: string): string {
    const typeMap: Record<string, string> = {
        'Conversation': 'text',
        'ExtendedTextMessage': 'text',
        'ImageMessage': 'image',
        'VideoMessage': 'video',
        'AudioMessage': 'audio',
        'PttMessage': 'audio',
        'DocumentMessage': 'document',
        'StickerMessage': 'sticker',
        'ContactMessage': 'contact',
        'LocationMessage': 'location',
        'ReactionMessage': 'reaction',
    };
    return typeMap[uazapiType] || 'text';
}
